import { useListenToMessage } from '@openeducation/oe-rn-hooks';
import { MediaEventType } from '@openeducation/oe-rn-shared-types';
import Player from '@vimeo/player';
import React, { useEffect, useRef } from 'react';

import type { VimeoVideoProps } from './vimeo-video-props';
import { useCallbackRef } from '../../hooks/use-callback-ref';

/**
 * This component shouldn't be used directly, use instead VimeoPlayer in vimeo-player.tsx
 */
const VimeoVideo = ({
  src,
  width,
  height,
  onEnded,
  onError,
  onLoaded,
  onPause,
  onPlay,
  onTimeUpdate,
}: VimeoVideoProps) => {
  const playerElementRef = useRef<HTMLIFrameElement>(null);
  const playerRef = useRef<Player>();

  useListenToMessage<MediaEventType>(
    () => playerRef.current?.pause(),
    MediaEventType.STOP_ALL_VIDEOS
  );

  const onEndedRef = useCallbackRef(onEnded);
  const onErrorRef = useCallbackRef(onError);
  const onLoadedRef = useCallbackRef(onLoaded);
  const onPauseRef = useCallbackRef(onPause);
  const onPlayRef = useCallbackRef(onPlay);
  const onTimeUpdateRef = useCallbackRef(onTimeUpdate);

  useEffect(() => {
    if (!playerElementRef.current) {
      return;
    }

    const player = new Player(playerElementRef.current);
    playerRef.current = player;

    player.on('ended', (e) => onEndedRef.current?.(e));
    player.on('error', (e) => onErrorRef.current?.(e));
    player.on('loaded', (e) => onLoadedRef.current?.(e));
    player.on('pause', (e) => onPauseRef.current?.(e));
    player.on('play', (e) => onPlayRef.current?.(e));
    player.on('timeupdate', (e) => onTimeUpdateRef.current?.(e));

    return () => {
      player.destroy();
    };
  }, [
    onEndedRef,
    onErrorRef,
    onLoadedRef,
    onPauseRef,
    onPlayRef,
    onTimeUpdateRef,
  ]);

  return (
    <iframe
      ref={playerElementRef}
      src={src}
      width={width}
      height={height}
      allow="autoplay; fullscreen"
      frameBorder="0"
      allowFullScreen
      // eslint-disable-next-line react-native/no-inline-styles
      style={{ border: 'none', marginLeft: 'auto', marginRight: 'auto' }}
    />
  );
};

export default VimeoVideo;
